import { Link } from "react-router-dom";
import { ROUTES } from "@/config/routes";
import { exportDisplayTitle } from "@/domain/export/formatExportRow";
import {
  formatExportDuration,
  formatPdfsCell,
} from "@/domain/export/formatExportList";
import { ExportFilterCriteriaCell } from "@/features/exports/components/ExportFilterCriteriaCell";
import { ExportDetailCell } from "@/features/exports/components/ExportDetailCell";
import { ExportDownloadCell } from "@/features/exports/components/ExportDownloadCell";
import { ExportStatusBadge } from "@/features/exports/components/ExportStatusBadge";
import {
  EXPORT_FAILED_DETAIL_HINT,
  EXPORT_FAILED_LIST_HINT,
} from "@/domain/export/exportFailedCopy";
import {
  DataTableShell,
  tableClass,
  tdClass,
  thClass,
} from "@/features/exports/components/DataTableShell";
import { formatDateTime } from "@/utils/format/date";
import type { ExportJobDetail } from "@/types/api";

const downloadColClass = `${tdClass} whitespace-nowrap text-sm`;
const downloadHeadClass = `${thClass} whitespace-nowrap`;
const detailColClass = `${tdClass} w-px whitespace-nowrap text-center`;
const detailHeadClass = `${thClass} w-px whitespace-nowrap text-center`;

export function ExportsTable({
  items,
  embedded = false,
}: {
  items: ExportJobDetail[];
  embedded?: boolean;
}) {
  if (!items.length) return null;

  return (
    <DataTableShell embedded={embedded}>
      <table className={`${tableClass} exports-table`}>
          <thead>
            <tr>
              <th className={thClass}>Export</th>
              <th className={thClass}>Status</th>
              <th className={thClass}>PDFs</th>
              <th className={thClass}>Duration</th>
              <th className={thClass}>Criteria</th>
              <th className={thClass}>Created</th>
              <th className={downloadHeadClass}>Download</th>
              <th className={detailHeadClass}>Detail</th>
            </tr>
          </thead>
          <tbody>
            {items.map((job) => (
              <tr key={job.job_id}>
                <td className={tdClass}>
                  <Link
                    to={ROUTES.exportDetail(job.job_id)}
                    className="link-quiet font-semibold"
                  >
                    {exportDisplayTitle(job)}
                  </Link>
                </td>
                <td className={tdClass}>
                  <div className="flex flex-col items-start gap-1">
                    <ExportStatusBadge status={job.status} />
                    {job.status === "FAILED" ? (
                      <span
                        className="text-[0.75rem] leading-snug text-[var(--muted)]"
                        title={EXPORT_FAILED_DETAIL_HINT}
                      >
                        {EXPORT_FAILED_LIST_HINT}
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className={`${tdClass} tabular-nums text-sm`}>
                  {formatPdfsCell(job)}
                </td>
                <td className={`${tdClass} text-sm text-[var(--muted)] whitespace-nowrap tabular-nums`}>
                  {formatExportDuration(job)}
                </td>
                <td className={`${tdClass} align-top`}>
                  <ExportFilterCriteriaCell job={job} />
                </td>
                <td className={`${tdClass} text-sm text-[var(--muted)] whitespace-nowrap tabular-nums`}>
                  {formatDateTime(job.created_at)}
                </td>
                <td className={downloadColClass}>
                  <ExportDownloadCell job={job} />
                </td>
                <td className={detailColClass}>
                  <ExportDetailCell job={job} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
    </DataTableShell>
  );
}
